/**
 * Imperative scroll choreography for revealing a page row in the Pages
 * inventory without the outer workspace jumping. Coupled by class name to
 * ScanShell's `.signal-workspace` scroller and PagesInventoryPane's
 * `.signal-page-inventory` list — renaming either breaks reveal-in-list.
 */

type WorkspaceAnchor = {
  element: HTMLElement;
  overflowAnchor: string;
  scrollTop: number;
};

/** Snapshot the workspace scroller and pin its scroll anchoring while the
 * inventory re-renders; scrollInventoryToPage restores and releases it. */
export function captureWorkspaceAnchor(): WorkspaceAnchor | null {
  const workspace = document.querySelector<HTMLElement>(".signal-workspace");
  if (!workspace) {
    return null;
  }
  const anchor = {
    element: workspace,
    overflowAnchor: workspace.style.overflowAnchor,
    scrollTop: workspace.scrollTop,
  };
  workspace.style.overflowAnchor = "none";
  return anchor;
}

/** Center the row for `pageId` in the inventory list after the next two
 * frames (filters may have just changed), keeping the workspace still. */
export function scrollInventoryToPage(
  pageId: string,
  workspaceAnchor: WorkspaceAnchor | null,
): void {
  const restoreWorkspace = (release = false) => {
    if (!workspaceAnchor) {
      return;
    }
    workspaceAnchor.element.scrollTop = workspaceAnchor.scrollTop;
    if (release) {
      workspaceAnchor.element.style.overflowAnchor =
        workspaceAnchor.overflowAnchor;
    }
  };
  requestAnimationFrame(() => {
    restoreWorkspace();
    requestAnimationFrame(() => {
      restoreWorkspace();
      const row = document.querySelector<HTMLElement>(
        `[data-page-id="${pageId}"]`,
      );
      if (!row) {
        restoreWorkspace(true);
        return;
      }
      const inventory = row.closest(".signal-page-inventory");
      if (inventory instanceof HTMLElement) {
        const rowRect = row.getBoundingClientRect();
        const inventoryRect = inventory.getBoundingClientRect();
        inventory.scrollTop +=
          rowRect.top -
          inventoryRect.top -
          inventory.clientHeight / 2 +
          row.clientHeight / 2;
      }
      restoreWorkspace(true);
    });
  });
}
